import { createAsyncThunk } from '@reduxjs/toolkit';

import { setLoading } from 'src/components/common/loading/loadingSlice';
import {
	IAnnotationLabel,
	IAnnotationLabelShortInfo,
	IGetAnnotationLabelsShortInfoThunkArgs,
	ICreateAnnotationLabelThunkArgs,
	IGetAnnotationLabelsThunkArgs,
	IGetAnnotationLabelThunkArgs,
	IUpdateAnnotationLabelThunkArgs,
} from 'src/features/annotation_label/domain/types';
import { annotationLabelApi } from 'src/features/annotation_label/infrastructure/api';
import { handleThunkError } from 'src/utils/handleThunkError';

export const ANNOTATION_LABEL_LOADING_KEY = 'annotation_label';

export const createAnnotationLabel = createAsyncThunk<
	IAnnotationLabel,
	ICreateAnnotationLabelThunkArgs,
	{ rejectValue: IErrorResponse }
>(
	'annotation_label/createAnnotationLabel',
	async (
		{ request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_LABEL_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await annotationLabelApi.createAnnotationLabel(
				request,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to create annotation label',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_LABEL_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const updateAnnotationLabel = createAsyncThunk<
	IAnnotationLabel,
	IUpdateAnnotationLabelThunkArgs,
	{
		rejectValue: IErrorResponse;
	}
>(
	'annotation_label/updateAnnotationLabel',
	async (
		{ annotationLabelId, request, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_LABEL_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await annotationLabelApi.updateAnnotationLabel(
				annotationLabelId,
				request,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to update annotation label',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_LABEL_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getAnnotationLabels = createAsyncThunk<
	IPaginatedResponse<IAnnotationLabel>,
	IGetAnnotationLabelsThunkArgs,
	{
		rejectValue: IErrorResponse;
	}
>(
	'annotation_label/getAnnotationLabels',
	async (
		{ siteSlug, params, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_LABEL_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await annotationLabelApi.getAnnotationLabels(
				params,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch annotation labels',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_LABEL_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getAnnotationLabel = createAsyncThunk<
	IAnnotationLabel,
	IGetAnnotationLabelThunkArgs,
	{
		rejectValue: IErrorResponse;
	}
>(
	'annotation_label/getAnnotationLabel',
	async (
		{ annotationLabelId, siteSlug, onSuccess, onError },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_LABEL_LOADING_KEY,
					loading: true,
				}),
			);
			const response = await annotationLabelApi.getAnnotationLabel(
				annotationLabelId,
				siteSlug,
			);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch annotation label',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_LABEL_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);

export const getAnnotationLabelsShortInfo = createAsyncThunk<
	IAnnotationLabelShortInfo[],
	IGetAnnotationLabelsShortInfoThunkArgs,
	{
		rejectValue: IErrorResponse;
	}
>(
	'annotation_label/getAnnotationLabelsShortInfo',
	async ({ siteSlug, onSuccess, onError }, { dispatch, rejectWithValue }) => {
		try {
			dispatch(
				setLoading({
					key: ANNOTATION_LABEL_LOADING_KEY,
					loading: true,
				}),
			);
			const response =
				await annotationLabelApi.getAnnotationLabelsShortInfo(siteSlug);

			onSuccess?.(response);
			return response;
		} catch (error: unknown) {
			const err = handleThunkError(
				error,
				'Failed to fetch annotation labels short info',
			);
			onError?.(err);
			return rejectWithValue(err);
		} finally {
			dispatch(
				setLoading({
					key: ANNOTATION_LABEL_LOADING_KEY,
					loading: false,
				}),
			);
		}
	},
);
